import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut, User } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/shared/ThemeSwitcher";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const navKeys = [
  ["/", "nav_home"], ["/vip", "nav_vip"], ["/brokers", "nav_brokers"],
  ["/education", "nav_education"], ["/results", "nav_results"], ["/about", "nav_about"],
  ["/offers", "nav_offers"], ["/faq", "nav_faq"], ["/contact", "nav_contact"],
];

export function Header() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-white/10" : "bg-transparent"}`} data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <Logo size="h-8 lg:h-9" />
          <nav className="hidden lg:flex items-center gap-1">
            {navKeys.map(([path, key]) => (
              <NavLink key={path} to={path} end={path === "/"}
                data-testid={`nav-${key.replace("nav_", "")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? "text-brand-cyan" : "text-slate-300 hover:text-white"}`}>
                {t(key)}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <ThemeSwitcher />
            <LanguageSwitcher />
            {user ? (
              <>
                {user.role === "admin" && (
                  <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="header-admin-btn" className="text-slate-200">
                    {t("nav_admin")}
                  </Button>
                )}
                <Button onClick={() => navigate("/dashboard")} data-testid="header-dashboard-btn"
                  className="gradient-brand text-brand-navy font-semibold hover:opacity-90">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav_dashboard")}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login-btn" className="text-slate-200">
                  {t("nav_login")}
                </Button>
                <Button onClick={() => navigate("/signup")} data-testid="header-signup-btn"
                  className="gradient-brand text-brand-navy font-semibold hover:opacity-90 rounded-full px-5">
                  {t("nav_signup")}
                </Button>
              </>
            )}
          </div>
          <div className="flex lg:hidden items-center gap-2">
            <LanguageSwitcher />
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="p-2 text-white">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="lg:hidden glass border-t border-white/10 animate-fade-up" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navKeys.map(([path, key]) => (
              <NavLink key={path} to={path} end={path === "/"} onClick={() => setOpen(false)}
                className={({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "text-brand-cyan bg-white/5" : "text-slate-300"}`}>
                {t(key)}
              </NavLink>
            ))}
            <div className="pt-3 flex flex-col gap-2 border-t border-white/10 mt-2">
              <div className="flex items-center justify-between py-1" data-testid="mobile-theme-row">
                <span className="text-sm text-slate-400">{t("theme")}</span>
                <ThemeSwitcher showLabels />
              </div>
              {user ? (
                <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="gradient-brand text-brand-navy font-semibold w-full">
                  {t("nav_dashboard")}
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { setOpen(false); navigate("/login"); }} className="w-full border-white/15">{t("nav_login")}</Button>
                  <Button onClick={() => { setOpen(false); navigate("/signup"); }} className="gradient-brand text-brand-navy font-semibold w-full">{t("nav_signup")}</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
